import { ParticipantRole } from "./backend";

/**
 * Domain types for conference rooms. Kept free of any LiveKit / SDK types
 * so they can be imported everywhere including the Electron main process.
 * When the Rust backend arrives, only the adapters change — these types stay.
 */

export type RoomConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error';

export interface RoomInfo {
  /** Unique room id used to generate share links and LiveKit room names. */
  id: string;
  /** Human-readable name shown in the UI. */
  name: string;
  /** The share link other participants use to join. */
  shareUrl: string;
}

export interface Participant {
  /** LiveKit participant identity (userId). */
  identity: string;
  /** Display name shown in the tile. */
  name: string;
  /** Whether this participant is the local user. */
  isLocal: boolean;
  /** Whether the participant's camera is enabled. */
  isCameraEnabled: boolean;
  /** Whether the participant's microphone is enabled. */
  isMicEnabled: boolean;
  /** Whether this participant is currently the dominant speaker. */
  isSpeaking: boolean;
  /** Live video track for rendering, null when camera is off. */
  videoTrack: MediaStreamTrack | null;
  /** Live audio track, null when mic is off. */
  audioTrack: MediaStreamTrack | null;
  role?: ParticipantRole;
  backendId: string | null;
}

export interface RoomState {
  connectionState: RoomConnectionState;
  room: RoomInfo | null;
  participants: Participant[];
  localParticipant: Participant | null;
  error: string | null;
  /** Backend meeting id — needed for share-links and the WS connection. */
  meetingId: string | null;
  /** This participant's id, returned by POST /meetings/{id}/join. */
  participantId: string | null;
}

export const DEFAULT_ROOM_STATE: RoomState = {
  connectionState: 'disconnected',
  room: null,
  participants: [],
  localParticipant: null,
  error: null,
  meetingId: null,
  participantId: null,
};

export interface JoinRoomParams {
  roomName: string;
  participantName: string;
  token: string;
  livekitUrl: string;
  /** true, если это programmatic reconnect ради нового токена (MediaTokenIssued),
   *  а не первичный join. Влияет на то, сбрасывать ли room state в адаптере. */
  isTokenRefresh?: boolean;
  participantId?: string;
}

export interface VideoGrant {
  roomJoin: boolean;
  canPublish: boolean;
  canSubscribe: boolean;
  canPublishData: boolean;
  room: string;
}

export interface LiveKitJwt {
  iss: string;
  sub: string;
  name: string;
  nbf: number;
  exp: number;
  video: VideoGrant;
}